import cloneUtils from '@utils/clone.utils';
import cookieUtils from '@utils/cookie.utils';
import baseConfig from '@config/base.config';
import accountApis from '@/apis/config/graphql/account';
import { usePermission } from '@models/profile/useProfilePermission';

//ECAPI: https://ecdoc.ptengine.cn/api/global.html
class AnalyticsService {
    isDevEnv: boolean;
    areaIsEn: boolean;
    trackService: {
        ptx: any | null;
        mixpanel: any | null;
        gtag: any | null;
        ec: {
            fnName: string;
            data: any;
        } | null;

        identify: {
            uid: string | null;
            property: any | null;
        };
    };
    homeVersionEventList: string[];
    editorVersionEventList: string[];

    constructor() {
        this.areaIsEn = /(ptmind|ptengine)\.com/.test(location.host);
        this.isDevEnv = import.meta.env.NODE_ENV === 'development';
        this.trackService = {
            ptx: null,
            mixpanel: null,
            gtag: null,
            ec: null,
            identify: {
                uid: null,
                property: {
                    email: null
                }
            }
        };

        // 需要额外添加事件属性的事件列表
        this.homeVersionEventList = [
            'cp_add_click',
            'cp_add_select_type',
            'cp_template_create_click',
            'cp_template_detail_click',
            'cp_add_save',
            'cp_creative_save',
            'cp_set_live',
            'hm_add_click',
            'hm_add_success',
            'cv_add_click',
            'cv_add_success',
            'upgrade_click',
            'plan_pay_click',
            'plan_pay_success'
        ];
        this.editorVersionEventList = ['cp_add_click'];
    }

    _identifyUser() {
        const isSuperAccount = cookieUtils.getItem(baseConfig.SUPER_ACCOUNT);
        const { uid, property } = this.trackService.identify;

        if (isSuperAccount || !uid) return;

        // Ptengine Experience
        if (uid) window.ptengine?.identify?.(uid, property);

        // Camera
        const { email } = property || {};
        // if (email) window.srec?.('uid', email);

        //FS
        email &&
            window.FS &&
            window.FS.identify(uid, {
                email
            });

        // Hotjar
        window.hj && window.hj('identify', uid, { email });

        // Mixpanel
        const sendData = cloneUtils.deep(
            Object.assign({}, property, {
                $email: property?.email,
                $name: property?.name
            })
        );
        delete sendData.email;
        delete sendData.name;
        window.mixpanel && (window.mixpanel.identify(uid), window.mixpanel.people.set(sendData));

        // Sendinblue
        window.sib && (window.sib.email_id = email);

        // ec
        window.__ptec && window.__ptec.identifyUser(uid, property);
    }

    _ptx() {
        const { name, property } = this.trackService.ptx || {};
        if (name) {
            window.ptengine?.track?.(name, property);
            // console.log(name, property);
        }
    }

    _mixpanel() {
        const { name, property } = this.trackService.mixpanel || {};
        window.mixpanel && window.mixpanel.track(name, property);
    }

    _gtag() {
        const { send_to, actionType, transaction_td } = this.trackService.gtag || {};
        window.gtag &&
            (actionType === 'conversion'
                ? send_to &&
                  window.gtag('event', actionType, {
                      send_to,
                      transaction_td: transaction_td || ''
                  })
                : window.gtag('event', actionType));
    }

    _ec() {
        const { fnName, data } = this.trackService.ec || {};
        window.__ptec &&
            fnName &&
            typeof window.__ptec[fnName] === 'function' &&
            window.__ptec[fnName](data);

        // console.log('ec event:', fnName, data);
    }

    // _camera() {
    //     if (this.trackService.camera) window.srec?.('tag', this.trackService.camera);
    // }

    _reset() {
        this.trackService = {
            pte: null,
            ptx: null
        };
    }

    usePTX(name: string, property?: any) {
        const { hasHomePermission, hasExperienceV4Permission } = usePermission();

        const sid = cookieUtils.getItem(baseConfig.LAST_PROFILE_ID);
        const sendProperty = sid
            ? property
                ? Object.assign({}, property, { project_id: sid })
                : { project_id: sid }
            : property;

        if (this.homeVersionEventList.includes(name) && sendProperty) {
            sendProperty.home_version = hasHomePermission.value ? '02' : '01';
        }

        if (this.editorVersionEventList.includes(name) && sendProperty) {
            sendProperty.editor_version = hasExperienceV4Permission.value ? 'v4' : 'v2';
        }

        this.trackService.ptx = { name, property: sendProperty };
        this.useMixpanel(name, sendProperty);

        switch (name) {
            case 'cp_set_live':
                this.areaIsEn || this.useGtag('conversion', 'AW-877514784/dpFVCLLQiccCEKCgt6ID');
                break;
            default:
                this.useGtag(name);
                break;
        }
        return this;
    }

    useEC(fnName: string, data?: any) {
        this.trackService.ec = {
            fnName,
            data
        };
        return this;
    }

    useMixpanel(name: string, property?: any) {
        this.trackService.mixpanel = { name, property };
        return this;
    }

    useGtag(actionType: string, sendTo?: string, transaction_td?: string) {
        this.trackService.gtag = { send_to: sendTo, actionType, transaction_td };
        return this;
    }

    // useCamera(tag: any) {
    //     this.trackService.camera = tag;
    //     return this;
    // }

    identifyUser(userInfo: any, property?: any) {
        const {
            id,
            ptiUid,
            firstName,
            name,
            email,
            password,
            phone,
            industry,
            company,
            position,
            websiteType,
            token,
            hasActivated,
            hasAllow,
            createTime,
            activatedTime,
            updateTime,
            signupSource,
            permissionsDict,
            permissions,
            i18n,
            status,
            freeTrialLong,
            accountType,
            department,
            companySize,
            address,
            onlyPti,
            jobTitle,
            loginUtm,
            needSecondVerification,
            beFirstLogin,
            needGuide,
            ptiToken,
            ptiPwd,
            role,
            area,
            useType,
            beFromGoogle,
            isProductIntroPopup
        } = userInfo || {};

        property ||
            (property = {
                email, //'用户邮箱，即用户登录账户, 唯一',
                area, //'用户地区: (JP -> 日本)、(EN -> 欧美)、(ZH -> 中国)',
                status, // '用户状态: (0 -> 无效)、(1 -> 有效)、(2 -> 已删除)',
                create_time: createTime, //'用户创建时间，时间戳格式（13位）',
                firstName,
                name, // '用户名',
                phone, // '用户电话',
                industry, // '用户行业',
                company, // '用户公司名',
                website_type: websiteType,
                signup_source: signupSource, //'注册用户来源',
                is_migration: isProductIntroPopup
            });
        this.trackService.identify = {
            uid: id,
            property
        };

        // send;
        this._identifyUser();
    }

    sendLastProfile(
        uid: string,
        property: {
            [key: string]: any;
        }
    ) {
        window.ptengine?.identify?.(uid, property);

        //FS
        window.FS && window.FS.identify(uid, property);

        // Mixpanel
        window.mixpanel && (window.mixpanel.identify(uid), window.mixpanel.people.set(property));
    }

    mergeUser(uid: string) {
        // mixpanel
        window.mixpanel && window.mixpanel.alias(uid);
    }

    track() {
        const isSuperAccount = cookieUtils.getItem(baseConfig.SUPER_ACCOUNT);
        if (isSuperAccount) return;

        this._ptx();
        this._mixpanel();
        this._gtag();
        this._ec();
        this._reset();
    }

    sendProductActiveTime(product: 'pti' | 'ptx') {
        const uid =
            localStorage.getItem(baseConfig.ACCOUNT_ID) ||
            cookieUtils.getItem(baseConfig.ACCOUNT_ID);
        if (!uid) return;

        const aidLocalVal = JSON.parse(localStorage.getItem(baseConfig.PRODUCT_ACTIVE) || '{}');
        if (!aidLocalVal[uid] || !aidLocalVal[uid][product]) {
            accountApis
                .setProductActiveTime({
                    aid: uid,
                    product
                })
                .then(() => {
                    aidLocalVal[uid] = aidLocalVal[uid] || {};
                    aidLocalVal[uid][product] = Date.now();
                    localStorage.setItem(baseConfig.PRODUCT_ACTIVE, JSON.stringify(aidLocalVal));
                });
        }
    }
}

export default new AnalyticsService();
